import {View} from 'react-native'
import {type AppBskyActorDefs} from '@atproto/api'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'

import {isInvalidHandle, sanitizeHandle} from '#/lib/strings/handles'
import {type Shadow} from '#/state/cache/types'
import {
  findCreatorRole,
  findOrgType,
  ORG_TYPE_ICONS,
} from '#/screens/Onboarding/StepGoal/data'
import {atoms as a, useTheme, web} from '#/alf'
import {NewskieDialog} from '#/components/NewskieDialog'
import {Text} from '#/components/Typography'
import {IS_IOS, IS_NATIVE} from '#/env'

export function ProfileHeaderHandle({
  profile,
  disableTaps,
}: {
  profile: Shadow<AppBskyActorDefs.ProfileViewDetailed>
  disableTaps?: boolean
}) {
  const t = useTheme()
  const {i18n, _} = useLingui()
  const invalidHandle = isInvalidHandle(profile.handle)
  const blockHide = profile.viewer?.blocking || profile.viewer?.blockedBy
  const p = profile as {
    type?: string
    creatorRole?: string
    orgType?: string
  }
  let roleLabel: string | undefined
  if (p.type === 'organisation') {
    const orgType = findOrgType(p.orgType)
    const icon = p.orgType ? (ORG_TYPE_ICONS[p.orgType] ?? '🏢') : '🏢'
    roleLabel = orgType ? `${icon} ${i18n._(orgType.label)}` : `🏢 Organisation`
  } else if (p.type === 'parent') {
    roleLabel = '👨‍👩‍👧 Parent'
  } else if (p.type === 'learner') {
    roleLabel = '🎓 Learner'
  } else if (p.type === 'creator') {
    const creatorRoleEntry = findCreatorRole(p.creatorRole)
    if (creatorRoleEntry) {
      roleLabel = `${creatorRoleEntry.icon} ${i18n._(creatorRoleEntry.label)}`
    }
  }
  return (
    <View
      style={[a.flex_row, a.gap_sm, a.align_center, {maxWidth: '100%'}]}
      pointerEvents={disableTaps ? 'none' : IS_IOS ? 'auto' : 'box-none'}>
      <NewskieDialog profile={profile} disabled={disableTaps} />
      {profile.viewer?.followedBy && !blockHide ? (
        <View style={[t.atoms.bg_contrast_50, a.rounded_xs, a.px_sm, a.py_xs]}>
          <Text style={[t.atoms.text, a.text_sm]}>
            <Trans>Follows you</Trans>
          </Text>
        </View>
      ) : undefined}
      <View style={[a.flex_1, a.flex_col, a.gap_2xs, a.overflow_hidden]}>
        <Text
          emoji
          numberOfLines={1}
          style={[
            invalidHandle
              ? [
                  a.border,
                  a.text_xs,
                  a.px_sm,
                  a.py_xs,
                  a.rounded_xs,
                  {borderColor: t.palette.contrast_200},
                ]
              : [a.text_md, a.leading_snug, t.atoms.text_contrast_medium],
            web({
              direction: 'ltr',
              unicodeBidi: 'isolate',
            }),
          ]}>
          {invalidHandle
            ? _(msg`⚠Invalid Handle`)
            : sanitizeHandle(
                profile.handle,
                '@',
                // forceLTR handled by CSS above on web
                IS_NATIVE,
              )}
        </Text>
        {roleLabel && (
          <Text
            emoji
            numberOfLines={1}
            style={[a.text_sm, a.leading_snug, t.atoms.text_contrast_medium]}>
            {roleLabel}
          </Text>
        )}
      </View>
    </View>
  )
}
